<template>
  <div
    ref="dropdownRef"
    v-on-clickaway="handleClickaway"
    :class="[
      'absolute z-[1000] bg-white/30 dark:bg-slate-800/20 backdrop-blur-3xl shadow-xl rounded-lg border border-solid border-n-weak overflow-hidden min-w-[250px]',
      positionClasses
    ]"
    @click.stop
  >
    <!-- Search bar -->
    <div
      v-if="search"
      class="sticky top-0 bg-white/30 dark:bg-slate-800/30 backdrop-blur-sm border-b border-n-weak px-3 py-2 z-10"
    >
      <div class="flex items-center gap-2">
        <i class="i-lucide-search text-slate-400 flex-shrink-0 w-4 h-4"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Buscar..."
          class="w-full bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 outline-none"
        />
      </div>
    </div>

    <!-- Scrollable content -->
    <div
      class="overflow-y-auto p-2"
      :style="{ maxHeight: `${height}px` }"
    >
      <!-- Clear option -->
      <button
        v-if="clear"
        class="flex items-center justify-between w-full p-1.5 rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-slate-6 transition-colors duration-150 mb-1"
        @click="handleClear"
      >
        <div class="flex items-center gap-2">
          <div class="w-5 h-5 rounded-full bg-woot-100 flex items-center justify-center">
            <i class="i-lucide-x text-woot-600 text-xs" />
          </div>
          <span class="text-sm text-n-slate-12">Nenhum</span>
        </div>
        <i
          v-if="isNoneSelected"
          class="i-lucide-check w-4 h-4 text-n-slate-12"
        ></i>
      </button>

      <!-- Items list -->
      <button
        v-for="item in filteredItems"
        :key="item.id"
        class="flex items-center justify-between gap-3 w-full p-1.5 rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-slate-6 transition-colors duration-150 text-left"
        @click="handleSelect(item)"
      >
        <div class="flex items-center gap-3 min-w-0">
          <!-- Icon without background -->
          <div class="relative flex-shrink-0">
            <i
              v-if="item.icon"
              :class="[
                `i-lucide-${item.icon}`,
                'flex-shrink-0',
                item.destructive ? 'text-ruby-600' : 'text-n-slate-12'
              ]"
            ></i>
            <!-- Badge indicator -->
            <span
              v-if="item.badge"
              class="absolute -top-0.5 -right-0.5 w-2 h-2 bg-n-brand rounded-full"
            ></span>
          </div>

          <!-- Title and value -->
          <span
            class="text-sm truncate"
            :class="item.destructive ? 'text-ruby-600' : 'text-n-slate-12'"
          >
            {{ item.title }}
          </span>
        </div>

        <!-- Chevron indicator -->
        <i
          v-if="item.showChevron"
          class="i-lucide-chevron-right w-4 h-4 flex-shrink-0 text-n-slate-10"
        ></i>
      </button>

      <!-- Empty state -->
      <div
        v-if="filteredItems.length === 0 && searchQuery"
        class="px-4 py-8 text-center"
      >
        <p class="text-sm text-slate-500 dark:text-slate-400">
          Nenhum resultado encontrado
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';

const dropdownRef = ref(null);
const position = ref({ horizontal: 'right', vertical: 'bottom' });

const props = defineProps({
  items: {
    type: Array,
    required: true,
    validator: (items) => {
      return items.every(item =>
        item.hasOwnProperty('id') &&
        item.hasOwnProperty('title')
      );
    }
  },
  clear: {
    type: Boolean,
    default: false
  },
  search: {
    type: Boolean,
    default: false
  },
  height: {
    type: Number,
    default: 300
  },
  modelValue: {
    type: [String, Number, null],
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'select', 'clear', 'close']);

const searchQuery = ref('');

const filteredItems = computed(() => {
  if (!searchQuery.value) {
    return props.items;
  }

  const query = searchQuery.value.toLowerCase();
  return props.items.filter(item => {
    const titleMatch = item.title.toLowerCase().includes(query);
    const valueMatch = item.value ? item.value.toLowerCase().includes(query) : false;
    return titleMatch || valueMatch;
  });
});

const isNoneSelected = computed(() => {
  return !props.modelValue;
});

const handleSelect = (item) => {
  emit('update:modelValue', item.id);
  emit('select', item);
};

const handleClear = () => {
  emit('update:modelValue', null);
  emit('clear');
};

const handleClickaway = () => {
  emit('close');
};

const positionClasses = computed(() => {
  const classes = [];

  // Horizontal positioning
  if (position.value.horizontal === 'left') {
    classes.push('right-0');
  } else if (position.value.horizontal === 'center') {
    classes.push('left-1/2', '-translate-x-1/2');
  } else {
    // right (default)
    classes.push('left-0');
  }

  // Vertical positioning
  if (position.value.vertical === 'top') {
    classes.push('bottom-full', 'mb-2');
  } else {
    // bottom (default)
    classes.push('top-full', 'mt-2');
  }

  return classes;
});

const detectEdgePosition = async () => {
  await nextTick();

  if (!dropdownRef.value) return;

  const dropdown = dropdownRef.value;
  const rect = dropdown.getBoundingClientRect();
  const padding = 8; // Safety margin from edges

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Get parent element position to calculate relative positioning
  const parent = dropdown.parentElement;
  const parentRect = parent?.getBoundingClientRect();

  if (!parentRect) return;

  // Calculate where dropdown would be positioned for each option
  // and ensure 8px from screen edges

  // Horizontal edge detection
  const leftAlignedRight = parentRect.left + rect.width; // right edge when left-aligned (left-0)
  const rightAlignedLeft = parentRect.right - rect.width; // left edge when right-aligned (right-0)
  const centeredLeft = parentRect.left + parentRect.width / 2 - rect.width / 2;
  const centeredRight = centeredLeft + rect.width;

  // Check if dropdown would overflow right edge when left-aligned
  if (leftAlignedRight > viewportWidth - padding) {
    // Check if right-align would work
    if (rightAlignedLeft >= padding) {
      // Right-align works
      position.value.horizontal = 'left';
    } else if (centeredLeft >= padding && centeredRight <= viewportWidth - padding) {
      // Center works
      position.value.horizontal = 'center';
    } else {
      // Nothing fits perfectly, use right-align as lesser evil
      position.value.horizontal = 'left';
    }
  }
  // Check if dropdown would overflow left edge when right-aligned
  else if (parentRect.left < padding) {
    // Left-align works
    position.value.horizontal = 'right';
  }
  // Default: try to center if there's enough space
  else {
    if (centeredLeft >= padding && centeredRight <= viewportWidth - padding) {
      position.value.horizontal = 'center';
    } else if (parentRect.right < viewportWidth / 2) {
      // Parent is on left side of screen, align dropdown to left (left-0)
      position.value.horizontal = 'right';
    } else {
      // Parent is on right side of screen, align dropdown to right (right-0)
      position.value.horizontal = 'left';
    }
  }

  // Vertical edge detection
  // Check if dropdown would overflow bottom edge
  if (parentRect.bottom + rect.height + padding > viewportHeight) {
    // Position above
    position.value.horizontal = 'top';
  } else {
    // Position below (default)
    position.value.vertical = 'bottom';
  }
};

onMounted(() => {
  detectEdgePosition();
});
</script>
